/** Role keys used for permissions and access checks (values are DB role codes). */
export type RoleKey =
  | 'ADMIN'
  | 'EMPLOYEE'
  | 'HR'
  | 'FINANCE'
  | 'APPROVER'
  | 'LEGACY_EMPLOYEE'
  | 'LEGACY_ADMIN';

export type RoleDefinition = {
  readonly key: RoleKey;
  readonly id: number;
  /** DB / API role code - use for comparisons, guards, and API payloads */
  readonly code: string;
  /** Human-readable label - use everywhere in the UI */
  readonly name: string;
  readonly color: string;
};

export const ROLE_DEFINITIONS: readonly RoleDefinition[] = [
  { key: 'LEGACY_EMPLOYEE', id: 1, code: 'employee', name: 'Employee', color: '#059669' },
  { key: 'APPROVER', id: 2, code: 'approver', name: 'Approver', color: '#0891B2' },
  { key: 'FINANCE', id: 3, code: 'finance', name: 'Finance', color: '#D97706' },
  { key: 'LEGACY_ADMIN', id: 4, code: 'admin', name: 'Admin', color: '#219268' },
  { key: 'ADMIN', id: 9, code: 'QHRMS-ADMIN', name: 'Administrator', color: '#219268' },
  { key: 'EMPLOYEE', id: 10, code: 'QHRMS_EMPLOYEE', name: 'Employee', color: '#059669' },
  { key: 'HR', id: 11, code: 'HR', name: 'HR Manager', color: '#7C3AED' },
] as const;

/** QHRMS application roles (AppId-scoped in DB). */
export const QHRMS_ROLE_DEFINITIONS = ROLE_DEFINITIONS.filter(
  (r) => r.key === 'ADMIN' || r.key === 'EMPLOYEE' || r.key === 'HR',
);

/** Role codes - use for sidebar access, guards, and permission checks. */
export const ROLES = Object.fromEntries(
  ROLE_DEFINITIONS.map((r) => [r.key, r.code]),
) as { readonly [K in RoleKey]: string };

/** Role display names - use for labels in the UI. */
export const ROLE_NAMES = Object.fromEntries(
  ROLE_DEFINITIONS.map((r) => [r.key, r.name]),
) as { readonly [K in RoleKey]: string };

export const ROLE_IDS = Object.fromEntries(
  ROLE_DEFINITIONS.map((r) => [r.key, r.id]),
) as { readonly [K in RoleKey]: number };

export type ROLES = (typeof ROLES)[RoleKey];
export type RoleType = RoleKey;

const CODE_TO_DEFINITION = new Map(ROLE_DEFINITIONS.map((r) => [r.code, r]));

/** Resolve a role code to its display name. */
export function getRoleName(code?: string | null): string {
  if (!code) return ROLE_NAMES.EMPLOYEE;
  return CODE_TO_DEFINITION.get(code)?.name ?? code;
}

/** @deprecated Use getRoleName - kept for existing imports */
export const ROLE_LABELS: Record<ROLES, string> = Object.fromEntries(
  ROLE_DEFINITIONS.map((r) => [r.code, r.name]),
) as Record<ROLES, string>;

export const ROLE_COLORS: Record<ROLES, string> = Object.fromEntries(
  ROLE_DEFINITIONS.map((r) => [r.code, r.color]),
) as Record<ROLES, string>;
